import type { UseFormPayload } from '@domain-group/react-hooks'
import { useSetState } from '@domain-group/react-hooks'

import usePaginationRules from '@hooks/usePaginationRules'
import usePublicationPaginationRule, { usePublicationPaginationRuleForm } from '@hooks/usePublicationPaginationRule'
import usePublicationPaginationRules from '@hooks/usePublicationPaginationRules'

import type { BrandModel, PublicationPaginationRuleModel } from '@models/types'

type HandleBrandSelectorParams = {
  brand?: BrandModel,
  setState: Function,
  setEntityState: UseFormPayload['setEntityState'],
}

const handleBrandSelector = (params: HandleBrandSelectorParams) => {
  const { brand, setState, setEntityState } = params

  if (!brand){
    setEntityState({ pagination_rule_id: undefined })
    setState({ brandId: null })
    return
  }

  setState({ brandId: brand.id })
}

type DefaultState = {
  brandId?: number,
}

const defaultState: DefaultState = {
  brandId: undefined,
}

interface CreatePublicationPaginationRuleModalProps {
  closeModal: () => void,
  publicationPaginationRule: PublicationPaginationRuleModel,
}

const useCreatePublicationPaginationRuleModal = (props: CreatePublicationPaginationRuleModalProps) => {
  const { closeModal, publicationPaginationRule } = props

  const [state, setState] = useSetState(defaultState)
  const { brandId } = state

  const {
    callbacks: { createPublicationPaginationRule: createFn },
    creating,
  } = usePublicationPaginationRule(publicationPaginationRule)

  const publicationPaginationRuleFormPayload = usePublicationPaginationRuleForm(
    publicationPaginationRule,
    { validateOn: 'change' },
  )
  const { entityState, setEntityState } = publicationPaginationRuleFormPayload

  const {
    filteredPaginationRules: brandPaginationRules,
  } = usePaginationRules({ filters: { brand_id: brandId } })
  const {
    filteredPublicationPaginationRules,
  } = usePublicationPaginationRules({ filters: { publication_id: publicationPaginationRule.publication_id } })

  const assignedPaginationRuleIds = filteredPublicationPaginationRules.map(item => item.pagination_rule_id)
  const unassignedPaginationRules = brandPaginationRules.filter(
    paginationRule => !assignedPaginationRuleIds.includes(paginationRule.id),
  )

  const createPublicationPaginationRulePayload = {
    callbacks: {
      action: createFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: 'Pagination Rule Added',
  }

  return (
    {
      callbacks: {
        handleBrandSelector: (brand?: BrandModel) => handleBrandSelector({ brand, setState, setEntityState }),
      },
      brandId,
      createPublicationPaginationRulePayload,
      creating,
      publicationPaginationRuleFormPayload,
      unassignedPaginationRules,
    }
  )
}

export default useCreatePublicationPaginationRuleModal
